import axios from 'axios';

import type {
  Interval,
  PreferredFunction,
  RosterFunction
} from 'components/types';

import { toLocalISOString } from 'utils/date';

import {
  COST_CENTERS_FUNCTIONS,
  PREFERRED_FUNCTIONS,
  ROSTER_FUNCTIONS
} from 'RestAPIs/endpoints';

export const getRosterFunctions = (interval?: Interval) => {
  const params: any = interval
    ? {
        startDate: toLocalISOString(interval.startDate),
        endDate: toLocalISOString(interval.endDate)
      }
    : {};

  return axios.get<RosterFunction[]>(ROSTER_FUNCTIONS, {
    params
  });
};

type GetPreferredFunctionsOptions = {
  costCenterIds: number[];
};

export const getPreferredFunctions = ({
  costCenterIds
}: GetPreferredFunctionsOptions) =>
  axios.get<PreferredFunction[]>(PREFERRED_FUNCTIONS, {
    params: {
      costCenters: costCenterIds.join(',')
    }
  });

type GetRosterFunctionsWithPreferredOptions = {
  interval?: Interval;
  costCenterIds: number[];
};

export const getRosterFunctionsWithPreferred = ({
  costCenterIds,
  interval
}: GetRosterFunctionsWithPreferredOptions) =>
  Promise.all<any>([
    getRosterFunctions(interval),
    getPreferredFunctions({ costCenterIds })
  ]).then(([rosterFunctions, preferredFunctions]) => {
    const { data: rfData } = rosterFunctions;
    const { data: pfData } = preferredFunctions;

    const preferredFunctionIds =
      pfData.length > 0 ? pfData[0].functions.map(({ id }) => id) : [];
    const withPreferred = rfData.map((rf) => ({
      ...rf,
      preferred: preferredFunctionIds.includes(rf.id)
    }));
    const data = withPreferred.sort((a, b) => b.preferred - a.preferred);

    return { data };
  });

export type RosterFunctionByCostCenterAndDate = {
  costCenterId: number;
  startDate: string;
  endDate: string;
  functions?: FunctionTypes[] | null;
};

export type FunctionTypes = {
  id: number;
};

export const getRosterFunctionsByCostCenterAndDate = ({
  costCenterId,
  startDate,
  endDate
}: RosterFunctionByCostCenterAndDate) => {
  return axios.get<RosterFunctionByCostCenterAndDate[]>(
    `${ROSTER_FUNCTIONS}?costCenters=${costCenterId}&startDate=${startDate}&endDate=${endDate}`
  );
};

export const getRosterFunctionsPreferred = ({
  costCenterId,
  startDate,
  endDate
}: RosterFunctionByCostCenterAndDate) => {
  return axios.get<RosterFunctionByCostCenterAndDate[]>(
    `${PREFERRED_FUNCTIONS}?costCenters=${costCenterId}&startDate=${startDate}&endDate=${endDate}`
  );
};

export const addRosterFunctions = (
  costCenterId: number,
  functionId: number
) => {
  const url = COST_CENTERS_FUNCTIONS.replace(
    ':costCenterId',
    costCenterId.toString()
  );

  return axios.post<RosterFunctionByCostCenterAndDate>(
    `${url}?functionId=${functionId}`
  );
};

export const removeRosterFunctions = (
  costCenterId: number,
  functionId: number
) => {
  const url = COST_CENTERS_FUNCTIONS.replace(
    ':costCenterId',
    costCenterId.toString()
  );

  return axios.delete<RosterFunctionByCostCenterAndDate>(
    `${url}?functionId=${functionId}`
  );
};
